import { useState } from "react";
import { KeyRound, Mail, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const inputCls =
    "w-full bg-[#0A1228]/70 border border-white/10 rounded-xl py-3 px-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

const submitCls =
    "w-full flex items-center justify-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl py-3 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50";

export const AccountPanel = ({ user, onUserUpdate }) => {
    const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [emailForm, setEmailForm] = useState({ new_email: "", password: "" });
    const [savingPw, setSavingPw] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);

    const errText = (e, fallback) => {
        const d = e.response?.data?.detail;
        return typeof d === "string" ? d : fallback;
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        if (pwForm.new_password !== pwForm.confirm_password) {
            toast.error("كلمتا المرور الجديدتان غير متطابقتين");
            return;
        }
        setSavingPw(true);
        try {
            const { data } = await api.post("/auth/change-password", pwForm);
            localStorage.setItem("thuraya_admin_token", data.token);
            setPwForm({ current_password: "", new_password: "", confirm_password: "" });
            toast.success("تم تغيير كلمة المرور بنجاح");
        } catch (err) {
            toast.error(errText(err, "تعذر تغيير كلمة المرور"));
        } finally {
            setSavingPw(false);
        }
    };

    const submitEmail = async (e) => {
        e.preventDefault();
        setSavingEmail(true);
        try {
            const { data } = await api.post("/auth/change-email", emailForm);
            localStorage.setItem("thuraya_admin_token", data.token);
            onUserUpdate(data.user);
            setEmailForm({ new_email: "", password: "" });
            toast.success("تم تغيير البريد الإلكتروني بنجاح");
        } catch (err) {
            toast.error(errText(err, "تعذر تغيير البريد الإلكتروني"));
        } finally {
            setSavingEmail(false);
        }
    };

    return (
        <div className="space-y-8 max-w-2xl" data-testid="admin-account-panel">
            <div>
                <h2 className="font-display font-bold text-xl text-white">إعدادات الحساب</h2>
                <p className="text-sm text-slate-500 mt-1">إدارة بيانات دخول المدير — الحساب الحالي: <span className="text-amber-400" dir="ltr">{user?.email}</span></p>
            </div>

            <form onSubmit={submitPassword} className="spotlight-card border border-white/10 rounded-3xl p-6 space-y-4" data-testid="change-password-form">
                <h3 className="font-display font-bold text-white flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-amber-400" /> تغيير كلمة المرور
                </h3>
                <input
                    type="password"
                    required
                    value={pwForm.current_password}
                    onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                    placeholder="كلمة المرور الحالية"
                    data-testid="current-password-input"
                    className={inputCls}
                />
                <input
                    type="password"
                    required
                    minLength={8}
                    value={pwForm.new_password}
                    onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                    placeholder="كلمة المرور الجديدة (8 أحرف على الأقل)"
                    data-testid="new-password-input"
                    className={inputCls}
                />
                <input
                    type="password"
                    required
                    minLength={8}
                    value={pwForm.confirm_password}
                    onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                    placeholder="تأكيد كلمة المرور الجديدة"
                    data-testid="confirm-password-input"
                    className={inputCls}
                />
                <button type="submit" disabled={savingPw} data-testid="change-password-submit" className={submitCls}>
                    <Check className="w-4 h-4" /> {savingPw ? "جاري الحفظ..." : "تغيير كلمة المرور"}
                </button>
            </form>

            <form onSubmit={submitEmail} className="spotlight-card border border-white/10 rounded-3xl p-6 space-y-4" data-testid="change-email-form">
                <h3 className="font-display font-bold text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-400" /> تغيير البريد الإلكتروني
                </h3>
                <input
                    type="email"
                    required
                    value={emailForm.new_email}
                    onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                    placeholder="البريد الإلكتروني الجديد"
                    data-testid="new-email-input"
                    className={inputCls}
                    dir="ltr"
                />
                <input
                    type="password"
                    required
                    value={emailForm.password}
                    onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                    placeholder="كلمة المرور للتأكيد"
                    data-testid="email-password-input"
                    className={inputCls}
                />
                <button type="submit" disabled={savingEmail} data-testid="change-email-submit" className={submitCls}>
                    <Check className="w-4 h-4" /> {savingEmail ? "جاري الحفظ..." : "تغيير البريد الإلكتروني"}
                </button>
            </form>

            <div className="flex items-start gap-3 border border-emerald-500/20 bg-emerald-500/5 rounded-2xl p-4">
                <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400 leading-relaxed">
                    كلمات المرور مشفرة بالكامل (bcrypt) ولا تُخزن أو تظهر أبداً كنص. احتفظ ببيانات الدخول الجديدة في مكان آمن — عند نسيانها لن تتمكن من الدخول للوحة.
                </p>
            </div>
        </div>
    );
};
